import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CalendarCheck, TrendingUp, CreditCard, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Overview() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/classes/bookings/me"), api.get("/progress/me")])
      .then(([b, p]) => {
        setBookings(b.data);
        setProgress(p.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.status === "confirmed" && b.date >= today).slice(0, 3);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-overview-page">
      <Reveal>
        <p className="font-accent italic text-gold text-lg mb-1">Welcome back</p>
        <h1 className="font-anton uppercase tracking-tighter text-cream text-3xl sm:text-4xl">{user?.name}</h1>
      </Reveal>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <Reveal className="bg-surface border border-line p-6">
          <CreditCard className="h-6 w-6 text-gold mb-3" />
          <p className="text-smoke text-xs uppercase tracking-widest">Membership</p>
          <p className="text-cream text-lg font-semibold mt-1 capitalize">
            {user?.membership_status === "active" ? user?.membership_plan?.replace(/_/g, " ") : "Inactive"}
          </p>
        </Reveal>
        <Reveal delay={0.05} className="bg-surface border border-line p-6">
          <CalendarCheck className="h-6 w-6 text-gold mb-3" />
          <p className="text-smoke text-xs uppercase tracking-widest">Total Bookings</p>
          <p className="text-cream text-lg font-semibold mt-1">{bookings.filter((b) => b.status === "confirmed").length}</p>
        </Reveal>
        <Reveal delay={0.1} className="bg-surface border border-line p-6">
          <TrendingUp className="h-6 w-6 text-gold mb-3" />
          <p className="text-smoke text-xs uppercase tracking-widest">PRs Logged</p>
          <p className="text-cream text-lg font-semibold mt-1">{progress.length}</p>
        </Reveal>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-anton uppercase text-cream text-xl">Upcoming Classes</h2>
          <Link to="/schedule" className="text-gold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="overview-book-more-link">
            Book more <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-smoke text-sm">No upcoming classes booked yet.</p>
        ) : (
          <div className="space-y-3">
            {upcoming.map((b) => (
              <div key={b.id} className="bg-surface border border-line p-4" data-testid={`overview-booking-${b.id}`}>
                <p className="text-cream font-semibold">{b.class_type}</p>
                <p className="text-smoke text-sm">
                  {b.date} at {b.start_time}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
